/**
 * The email somebody who cannot get in sends to OneConetec support — prefilled
 * (David, 8 Oct).
 *
 * Since b8a83d5 the sign-in screen and NoAccess name SUPPORT_ADDRESS in plain
 * text. Until 11 Oct that mailbox was portal@conetec.com, shared with the CDS automation —
 * receivables, aging summaries, asset transfers, data submissions, Bamboo
 * new-hire notices, ~300 messages a week — and the support run (Step 6 of
 * docs/support-triage-run.md in the docs repo) has to find the access requests
 * in it. Since 11 Oct it is hr@conetec.com (David), where the tag does the same
 * job for HR. A free-text email in any language with any subject is a guess; a
 * fixed tag in the subject is a filter. So the address is a mailto link that
 * carries:
 *
 *  - SUPPORT_SUBJECT_TAG, the same in every language. The run keys on it.
 *    CHANGE IT HERE AND IN THE RUN'S PROMPT TOGETHER, or the run stops seeing
 *    requests and reports an empty week.
 *  - a reason code (ACCESS-NOT-FOUND, ACCESS-NO-CODE, ACCESS-INACTIVE) so the
 *    run knows which screen they were on without reading prose.
 *  - the address or number they typed, so support checks THAT and not the
 *    mailbox they happened to write from (the Lankelma .com/.co.uk split is
 *    exactly that difference, 135).
 *
 * The person sends it themselves from their own mail client, so nothing here
 * leaves the browser on its own. Someone without a mail client on the device
 * still sees the address and can type it — the link is a convenience, never
 * the only route.
 */
import { SUPPORT_ADDRESS } from './signInErrors'
import type { SignInLang } from './signInCopy'

/** Language-neutral on purpose: the support run filters on it. */
export const SUPPORT_SUBJECT_TAG = '[OneConetec access]'

export type SupportReason = 'not_found' | 'no_code' | 'inactive'

export const SUPPORT_REASON_CODE: Record<SupportReason, string> = {
  not_found: 'ACCESS-NOT-FOUND',
  no_code: 'ACCESS-NO-CODE',
  inactive: 'ACCESS-INACTIVE',
}

type MailWords = {
  subject: Record<SupportReason, string>
  what: Record<SupportReason, string>
  tried: string
  method: { email: string; phone: string }
  nameLine: string
  officeLine: string
  ref: string
}

const WORDS: Record<SignInLang, MailWords> = {
  en: {
    subject: {
      not_found: "I can't sign in: address not found",
      no_code: "I can't sign in: no code arrived",
      inactive: 'My access is not active',
    },
    what: {
      not_found: 'The sign-in screen said it could not find what I typed.',
      no_code: 'I asked for a sign-in code and it never arrived.',
      inactive: 'I signed in and was told my access is not active.',
    },
    tried: 'What I typed',
    method: { email: 'email', phone: 'mobile' },
    nameLine: 'My name:',
    officeLine: 'My office or business unit:',
    ref: 'Reference',
  },
  es: {
    subject: {
      not_found: 'No puedo iniciar sesión: no encuentra mi dirección',
      no_code: 'No puedo iniciar sesión: no llegó el código',
      inactive: 'Mi acceso no está activo',
    },
    what: {
      not_found: 'La pantalla de inicio de sesión dijo que no encontraba lo que escribí.',
      no_code: 'Pedí un código para iniciar sesión y nunca llegó.',
      inactive: 'Inicié sesión y me dijo que mi acceso no está activo.',
    },
    tried: 'Lo que escribí',
    method: { email: 'correo', phone: 'celular' },
    nameLine: 'Mi nombre:',
    officeLine: 'Mi oficina o unidad de negocio:',
    ref: 'Referencia',
  },
}

export function supportMailto(args: {
  reason: SupportReason
  lang: SignInLang
  method?: 'email' | 'phone'
  /** The address or E.164 number they typed, if the screen has one. */
  tried?: string | null
}): string {
  const w = WORDS[args.lang]
  const subject = `${SUPPORT_SUBJECT_TAG} ${w.subject[args.reason]}`
  const tried = args.tried?.trim()
  const lines = [
    w.what[args.reason],
    ...(tried ? [`${w.tried} (${w.method[args.method ?? 'email']}): ${tried}`] : []),
    '',
    w.nameLine,
    w.officeLine,
    '',
    `${w.ref}: ${SUPPORT_REASON_CODE[args.reason]}`,
  ]
  // encodeURIComponent, never URLSearchParams: the latter writes spaces as
  // "+", which Outlook and Gmail show as literal plus signs in a mailto.
  return (
    `mailto:${SUPPORT_ADDRESS}` +
    `?subject=${encodeURIComponent(subject)}` +
    `&body=${encodeURIComponent(lines.join('\r\n'))}`
  )
}
